<template>
  <transition name="fade" mode="out-in">
    <div
      v-if="show"
      class="tw-fixed tw-z-40 tw-inset-0 tw-overflow-y-auto"
      :class="{ 'tw-dark': DARK_MODE }"
    >
      <div
        class="tw-flex tw-flex-col tw-h-full tw-items-center tw-justify-center tw-min-h-screencalc tw-text-center tw-text-theme"
        :class="{ 'tw-mx-4': $slots.top }"
      >
        <!-- modal bg -->
        <div
          class="tw-fixed tw-inset-0 tw-transition-opacity"
          @click="handleClickOutside"
        >
          <div class="tw-absolute tw-inset-0 tw-opacity-70 tw-bg-black" />
        </div>

        <slot name="top" />

        <!-- modal -->
        <div
          ref="modalIo"
          class="tw-w-full tw-align-bottom tw-text-left tw-shadow-xl tw-transform tw-transition-all tw-mx-4 sm:tw-mx-0 sm:tw-align-middle tw-outline-none"
          :class="[
            modalStyles,
            !imageGallery && modalWidth,
            imageGallery ? 'tw-max-h-90vh' : 'tw-max-h-80vh',
            {
              'tw-flex tw-flex-col': !imageGallery,
              'sm:tw-my-12': !$slots.top,
            },
          ]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          tabindex="0"
          @click.self="imageGallery && handleClickOutside()"
        >
          <div
            class="tw-w-full"
            :class="[
              ($slots.header || heading) && modalPadX,
              {
                'tw-py-6 tw-pl-8 tw-pr-12 tw-bg-theme tw-global--border-radius-t':
                  ($slots.header || heading) && !noPadding,
              },
              imageGallery && 'tw-absolute tw-top-0 tw-left-0',
            ]"
          >
            <!-- header slot -->
            <panel-header
              v-if="heading"
              :text="heading"
              :warning="warning"
              :danger="danger"
            />
            <slot v-else name="header" />

            <!-- close button -->
            <base-button
              v-if="hasCloseBtn || imageGallery"
              subtle
              input-btn
              no-bg
              :warning="warning"
              :danger="danger"
              icon="times"
              icon-size="lg"
              sr-only="Close modal"
              class="tw-absolute tw-z-2 hover:tw-text-primary"
              :class="[
                imageGallery
                  ? 'tw-text-h4 md:tw-text-h3 tw-top-3 tw-right-3'
                  : 'tw-top-0 tw-right-0 xs:tw-top-1 xs:tw-right-1',
              ]"
              @click="$emit('close:modal', !show)"
            >
            </base-button>
          </div>

          <div
            v-if="$slots.fullWidth"
            class="tw-mx-auto tw-relative tw-overflow-auto tw-util-scrollbar tw-w-full"
            :class="{ 'tw-bg-light': alt }"
          >
            <slot name="fullWidth" />
          </div>

          <!-- modal slot -->
          <div
            v-if="$slots.default"
            class="tw-mx-auto"
            :class="[
              modalPadY,
              modalPadX,
              customClass,
              imageGallery && modalWidth,
              {
                'tw-overflow-auto tw-util-scrollbar tw-w-full':
                  !imageGallery && !noScroll,
                'tw-overflow-hidden': noScroll && !showOverflow,
                'tw-overflow-visible tw-w-full': noScroll && showOverflow,
                'tw-w-full tw-h-full': fullWidth,
              },
            ]"
          >
            <slot />
          </div>

          <!-- footer slot -->
          <footer
            v-if="$slots.footer"
            class="tw-global--border-radius-b tw-pb-6"
            :class="modalPadX"
          >
            <div
              class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-end tw-items-center tw-w-full tw-space-y-2 sm:tw-space-y-0 sm:tw-space-x-4"
            >
              <base-button
                v-if="cancelButton"
                auto-width
                subtle
                @click="$emit('close:modal')"
              >
                Cancel
              </base-button>
              <slot name="footer" />
            </div>
          </footer>

          <slot name="controls" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import PanelHeader from '@components/PanelHeader.vue'
import { computed, watch, toRefs, ref } from 'vue'
import { DARK_MODE } from '@config/game.js'
import { onKeyUp } from '@vueuse/core'

export default {
  components: {
    PanelHeader,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'lg',
      validator: function (value) {
        return ['sm', 'md', 'lg'].includes(value)
      },
    },
    hasCloseBtn: {
      type: Boolean,
      default: false,
    },
    heading: {
      type: String,
      default: null,
    },
    customClass: {
      type: String,
      default: '',
    },
    imageGallery: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
    noScroll: {
      type: Boolean,
      default: false,
    },
    showOverflow: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: true,
    },
    success: {
      type: Boolean,
      default: false,
    },
    info: {
      type: Boolean,
      default: false,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    danger: {
      type: Boolean,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
    alt: {
      type: Boolean,
      default: false,
    },
    cancelButton: {
      type: Boolean,
      default: true,
    },
    // set to false if input within modal is being
    // focused  on load i.e. LoginEmailModal.vue
    autoFocusModal: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['close:modal'],
  setup(props, { emit }) {
    const {
      closeOnClickOutside,
      autoFocusModal,
      closeOnEscape,
      imageGallery,
      noPadding,
      primary,
      warning,
      success,
      danger,
      info,
      size,
      show,
      alt,
    } = toRefs(props)

    const modalIo = ref(null)

    const modalWidth = computed(() => {
      if (imageGallery.value) {
        return 'tw-max-w-5/6 xs:tw-max-w-3/4'
      }
      if (size.value === 'lg') {
        return 'tw-max-w-3xl' // 768px
      }
      if (size.value === 'md') {
        return 'tw-max-w-lg' // 512px
      }
      if (size.value === 'sm') {
        return 'tw-max-w-xs' // 320px
      }
      return 'tw-max-w-3xl'
    })

    const modalPadX = computed(() => {
      if (imageGallery.value || noPadding.value) {
        return ''
      }
      if (size.value === 'sm') {
        return 'tw-px-8'
      }
      return 'tw-px-8 sm:tw-px-10'
    })

    const modalPadY = computed(() => {
      if (imageGallery.value || noPadding.value) {
        return ''
      }
      return 'tw-py-6'
    })

    const modalStyles = computed(() => {
      if (imageGallery.value) {
        return 'tw-flex tw-items-center tw-h-full '
      }
      return 'tw-bg-theme-3 tw-inline-block'
    })

    const borderStyle = computed(() => {
      if (success.value) {
        return 'tw-border-success'
      }
      if (danger.value) {
        return 'tw-border-danger'
      }
      if (warning.value) {
        return 'tw-border-warning'
      }
      if (info.value) {
        return 'tw-border-info'
      }
      if (primary.value || alt.value) {
        return 'tw-border-primary'
      }
      return 'tw-border-primary'
    })

    onKeyUp('Escape', () => {
      if (show.value && closeOnEscape.value) {
        emit('close:modal')
      }
    })

    watch(
      show,
      (value) => {
        if (value) {
          // 160ms timeout to account for component transition to finish
          setTimeout(() => {
            if (autoFocusModal.value) {
              modalIo.value?.focus()
            }
          }, 160)
        }
      },
      { immediate: true }
    )

    function handleClickOutside() {
      if (closeOnClickOutside.value) {
        emit('close:modal')
      }
    }
    return {
      handleClickOutside,
      modalStyles,
      borderStyle,
      modalWidth,
      modalPadY,
      modalPadX,
      DARK_MODE,
      modalIo,
    }
  },
}
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0s 0s ease;
}
.fade-enter-from,
.fade-leave-active {
  opacity: 0;
  transition: opacity 0.15s 0.05s ease;
}
</style>
